import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import Date from '@/components/Date/Date';
import {
  ComplianceDocument,
  GameSchema,
  MarketingAssetSchema,
  WithFormatted,
} from '@/lib/types';
import { useNavigate } from 'react-router';
import GameStudioLogo from '@/components/GameCard/GameStudioLogo';
import ResilientImage from '@/components/ResilientImage/ResilientImage';
import ComplianceThumbnail from '@/components/FileExplorer/ComplianceThumbnail';
import ExactMatchBadge from '@/components/FileExplorer/ExactMatchBadge';
import {
  isExactNameMatch,
  shouldSuggestQuotes,
} from '@/lib/searchMatch';

const extractEmHighlight = <
  T extends Record<string, unknown> & {
    _formatted: Record<string, unknown>;
  },
>({
  _formatted,
}: T): string | null => {
  const match = Object.entries(_formatted).find(([, value]) =>
    hasEmHighlight(value),
  );
  if (!match) return null;
  return match[0];
};

const hasEmHighlight = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.includes('<em>');
  }

  if (Array.isArray(value)) {
    return value.some((nestedValue) => hasEmHighlight(nestedValue));
  }

  if (value != null && typeof value === 'object') {
    return Object.values(value).some((nestedValue) =>
      hasEmHighlight(nestedValue),
    );
  }

  return false;
};

const formatFormattedKey = (key: string) => {
  const spaced = key.replace(/_/g, ' ');
  return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatFileTypeLabel = (fileType: string) => {
  const trimmed = fileType.trim();
  if (!trimmed) return null;
  return formatFormattedKey(trimmed.toLowerCase());
};

type GamesProps = {
  variant: 'games';
  games: WithFormatted<GameSchema>[];
  maxItems?: number;
  onGamesClick?: (game: WithFormatted<GameSchema>) => void;
};

type ComplianceProps = {
  variant: 'compliance';
  compliance_documents: WithFormatted<ComplianceDocument>[];
  maxItems?: number;
  onComplianceClick?: (
    doc: WithFormatted<ComplianceDocument>,
  ) => void;
  searchQuery?: string;
};

type MarketingProps = {
  variant: 'marketing';
  marketing_assets: WithFormatted<MarketingAssetSchema>[];
  maxItems?: number;
  onMarketingClick?: (
    asset: WithFormatted<MarketingAssetSchema>,
  ) => void;
};

type ResourcesSearchPropositionsProps =
  | GamesProps
  | ComplianceProps
  | MarketingProps;

const ResourcesSearchPropositions = (
  props: ResourcesSearchPropositionsProps,
) => {
  const maxItems = props.maxItems;
  const navigate = useNavigate();

  if (props.variant === 'games') {
    const games =
      maxItems != null ? props.games.slice(0, maxItems) : props.games;
    if (!games.length) return null;

    return (
      <List role="listbox">
        {games.map((game) => {
          const handleClick = () => {
            if (props.onGamesClick) {
              props.onGamesClick(game);
              return;
            }
            navigate(`/game/${game.studio_game_id}`);
          };

          const emHighlightKey = extractEmHighlight(game);

          return (
            <Item
              key={game.id}
              role="option"
              tabIndex={0}
              onClick={handleClick}
              onKeyUp={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleClick();
                }
              }}
            >
              <ThumbnailImage
                src={game.photo_url}
                alt={game.game_name}
                aria-label={game.game_name}
              />
              <ItemContent>
                <PrimaryText>{game.game_name}</PrimaryText>
                <SecondaryText>
                  ID:&nbsp;
                  <StudioIconWrapper>
                    <GameStudioLogo
                      gameProvider={game.game_provider}
                    />
                  </StudioIconWrapper>
                  <SecondaryStrong>
                    {game.studio_game_id}
                  </SecondaryStrong>
                </SecondaryText>
                {emHighlightKey ? (
                  <SecondaryText>
                    <FormattedMessage
                      id="foundResultsIn"
                      defaultMessage="Found results in:"
                    />
                    &nbsp;
                    <SecondaryStrong>
                      {formatFormattedKey(emHighlightKey)}
                    </SecondaryStrong>
                  </SecondaryText>
                ) : null}
              </ItemContent>
            </Item>
          );
        })}
      </List>
    );
  }

  if (props.variant === 'compliance') {
    const documents =
      maxItems != null
        ? props.compliance_documents.slice(0, maxItems)
        : props.compliance_documents;
    if (!documents.length) return null;
    const { searchQuery } = props;

    return (
      <>
        <List role="listbox">
          {documents.map((doc) => {
            const handleClick = () => {
              props.onComplianceClick?.(doc);
            };

            return (
              <Item
                key={doc.id}
                role="option"
                tabIndex={0}
                onClick={handleClick}
                onKeyUp={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleClick();
                  }
                }}
              >
                <ComplianceSuggestionThumbnail
                  file={doc}
                  aria-label={doc.name}
                  priority
                />
                <ItemContent>
                  <NameRow>
                    <PrimaryText>{doc.name}</PrimaryText>
                    {searchQuery &&
                    isExactNameMatch(
                      doc.name,
                      searchQuery,
                      doc.extension,
                    ) ? (
                      <ExactMatchBadge />
                    ) : null}
                  </NameRow>
                  <SecondaryText>
                    <span>Updated:&nbsp;</span>
                    <SecondaryStrong>
                      <Date value={doc.updated_at} />
                    </SecondaryStrong>
                  </SecondaryText>
                </ItemContent>
              </Item>
            );
          })}
        </List>
        {searchQuery && shouldSuggestQuotes(searchQuery) ? (
          <QuotesTip>
            <FormattedMessage
              id="search.quotesTip"
              defaultMessage='Tip: wrap words in "quotes" to search for an exact phrase'
            />
          </QuotesTip>
        ) : null}
      </>
    );
  }

  const resolvedAssets =
    maxItems != null
      ? props.marketing_assets.slice(0, maxItems)
      : props.marketing_assets;
  if (!resolvedAssets.length) return null;

  return (
    <List role="listbox">
      {resolvedAssets.map((asset) => {
        const dimension = extractDimensionFromName(asset.name);
        const fileTypeLabel = formatFileTypeLabel(asset.file_type);
        const gameNames = asset.games
          ?.map((g) => g.game_name)
          .filter(Boolean);
        const gamesLabel =
          gameNames && gameNames.length ? gameNames.join(', ') : null;
        const handleClick = () => {
          props.onMarketingClick?.(asset);
        };

        return (
          <Item
            key={asset.id}
            role="option"
            tabIndex={0}
            onClick={handleClick}
            onKeyUp={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                handleClick();
              }
            }}
          >
            <ThumbnailImage
              src={asset.preview_url}
              alt={asset.name}
              aria-label={asset.name}
            />
            <ItemContent>
              <PrimaryText>{asset.name}</PrimaryText>
              <SecondaryText>
                {dimension && <span>{dimension}&nbsp;•&nbsp;</span>}
                <span>{asset.extension.toUpperCase()}</span>
              </SecondaryText>
              {gamesLabel ? (
                <SecondaryTextEllipsis title={gamesLabel}>
                  <span>Games:&nbsp;</span>
                  <SecondaryStrong>{gamesLabel}</SecondaryStrong>
                </SecondaryTextEllipsis>
              ) : null}
              <SecondaryText>
                <span>Updated:&nbsp;</span>
                <SecondaryStrong>
                  <Date value={asset.updated_at} />
                </SecondaryStrong>
              </SecondaryText>
              {fileTypeLabel ? (
                <SecondaryText>
                  <span>File Type:&nbsp;</span>
                  <SecondaryStrong>{fileTypeLabel}</SecondaryStrong>
                </SecondaryText>
              ) : null}
            </ItemContent>
          </Item>
        );
      })}
    </List>
  );
};

const extractDimensionFromName = (name: string): string | null => {
  const match = name.match(/(\d+)[xX](\d+)/);
  if (!match) return null;
  const [, width, height] = match;
  return `${width}x${height}`;
};

const List = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const Item = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.radii.xs,
  cursor: 'pointer',
  backgroundColor: theme.palette.grey50,
  transition:
    'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.red50,
    boxShadow: theme.shadows[1],
  },
}));

const ThumbnailImage = styled(ResilientImage)({
  width: 32,
  height: 32,
  borderRadius: 4,
});

const ComplianceSuggestionThumbnail = styled(ComplianceThumbnail)({
  width: 32,
  height: 32,
  borderRadius: 4,
  flexShrink: 0,
});

const ItemContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

const NameRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

const PrimaryText = styled('div')(({ theme }) => ({
  minWidth: 0,
  fontSize: 13,
  lineHeight: '18px',
  fontWeight: 500,
  color: theme.palette.grey900,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

// Negative offsets undo the popper padding, which sticky respects.
const QuotesTip = styled('div')(({ theme }) => ({
  position: 'sticky',
  bottom: theme.spacing(-1.5),
  margin: theme.spacing(0.5, -1.5, -1.5),
  padding: theme.spacing(1, 2.5),
  backgroundColor: theme.palette.white,
  borderTop: `1px solid ${theme.palette.grey200}`,
  fontSize: 12,
  lineHeight: '16px',
  color: theme.palette.grey600,
}));

const SecondaryText = styled('div')(({ theme }) => ({
  fontSize: 12,
  lineHeight: '16px',
  color: theme.palette.grey600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),
}));

const SecondaryTextEllipsis = styled(SecondaryText)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const SecondaryStrong = styled('span')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.grey800,
}));

const StudioIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',

  svg: {
    height: 14,
  },
  path: {
    fill: theme.palette.grey600,
  },
}));

export default ResourcesSearchPropositions;
